import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation, Trans } from "react-i18next";
import {
  MODAL_PANEL_CLASS,
  MODAL_EASE,
} from "../common/modalMotion";

const GlobalMessageModal = ({ onClose = null }) => {
  const { t } = useTranslation("common");
  const [messageData, setMessageData] = useState(null);

  const dismiss = () => {
    setMessageData(null);
    onClose?.();
  };

  useEffect(() => {
    const errorHandler = (e) => setMessageData({ ...e.detail, type: "error" });
    const infoHandler = (e) => setMessageData({ ...e.detail, type: "info" });

    window.addEventListener("global-error", errorHandler);
    window.addEventListener("global-info", infoHandler);
    return () => {
      window.removeEventListener("global-error", errorHandler);
      window.removeEventListener("global-info", infoHandler);
    };
  }, []);

  useEffect(() => {
    if (!messageData) return;
    const rawMessage = String(messageData.message || "");
    const words = rawMessage.trim().split(/\s+/).filter(Boolean).length;
    const averageWordMs = 320;
    const minimumMs = messageData.type === "error" ? 5000 : 3200;
    const duration = Math.min(Math.max(words * averageWordMs, minimumMs), 9000);
    const timer = setTimeout(dismiss, duration);
    return () => clearTimeout(timer);
  }, [messageData]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const isError = messageData?.type === "error";
  const rawTrimmedMessage = String(messageData?.message || "").replace(/\.\s*$/, "");
  const isAccessDeniedMessage = /^access denied$/i.test(rawTrimmedMessage.trim());
  const trimmedMessage = isAccessDeniedMessage
    ? t("globalModal.access_denied", { defaultValue: "Access denied" })
    : rawTrimmedMessage;
  const SUPPORT_URL = "mailto:support@vaultgrid.net";

  const portalRoot =
    typeof document !== "undefined"
      ? document.getElementById("modal-root") || document.body
      : null;
  const workspaceScoped = Boolean(
    isDesktop && portalRoot && portalRoot !== document.body
  );

  const headerText = messageData?.status === 403
    ? t("globalModal.access_required")
    : isError
      ? t("globalModal.error", { defaultValue: "Error" })
      : t("globalModal.info");

  const shouldShowMessage = Boolean(trimmedMessage && trimmedMessage !== headerText);
  const showSupportText = Boolean(isError && Number(messageData?.status || 0) >= 500);
  const statusAccentClass = isError
    ? "bg-[#EF4458] shadow-[0_0_16px_rgba(239,68,88,0.38)]"
    : "bg-[#66B0FF] shadow-[0_0_16px_rgba(102,176,255,0.38)]";
  const headerTextStyle = isError
    ? { textShadow: "0 0 14px rgba(239,68,88,0.45)" }
    : undefined;
  const role = isError ? "alert" : "status";

  const content = (
    <div className={`pointer-events-none ${workspaceScoped ? "absolute" : "fixed"} inset-x-0 top-0 z-[15000] flex justify-center px-4 pt-[max(env(safe-area-inset-top),14px)] sm:pt-4`}>
      <AnimatePresence>
        {messageData ? (
          <motion.section
            className={`pointer-events-auto relative w-[min(calc(100vw-2rem),640px)] overflow-hidden rounded-br-[30px] rounded-tl-[30px] rounded-bl-[10px] rounded-tr-[10px] px-5 py-4 text-white sm:px-6 sm:py-5 ${MODAL_PANEL_CLASS}`}
            initial={{ opacity: 0, y: -24, scale: 0.96, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -16, scale: 0.98, filter: "blur(6px)" }}
            transition={{ duration: 0.34, ease: MODAL_EASE }}
            role={role}
            aria-live={isError ? "assertive" : "polite"}
          >
            <button
              type="button"
              onClick={dismiss}
              className="absolute right-2.5 top-2.5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white/88 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition active:scale-[0.96]"
              aria-label={t("globalModal.close", { defaultValue: "Close" })}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
              >
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className="relative">
              <div className="min-w-0 text-center">
                <div className="text-[17px] font-semibold leading-6 text-white sm:text-[19px]">
                  <span className="relative inline-flex items-center justify-center">
                    {isError ? (
                      <span
                        className="pointer-events-none absolute inset-x-[-18px] inset-y-[-10px] rounded-full bg-[#EF4458]/16 blur-[14px]"
                        aria-hidden="true"
                      />
                    ) : null}
                    <span
                      className={`absolute left-[-20px] top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ${statusAccentClass}`}
                      aria-hidden="true"
                    />
                    <span className="relative" style={headerTextStyle}>
                      {headerText}
                    </span>
                  </span>
                </div>
                {shouldShowMessage ? (
                  <div className="mt-1.5 whitespace-pre-line text-[14px] font-medium leading-6 text-white/82 sm:text-[15px]">
                    {trimmedMessage}
                  </div>
                ) : null}
                {messageData?.actionHref && messageData?.actionLabel ? (
                  <a
                    href={messageData.actionHref}
                    className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full border border-white/24 bg-white/12 px-5 text-[14px] font-semibold text-white transition hover:bg-white/18 active:scale-[0.98]"
                  >
                    {messageData.actionLabel}
                  </a>
                ) : null}
                {showSupportText ? (
                  <div className="mt-2 text-[13px] font-medium leading-5 text-white/68">
                    <Trans
                      i18nKey="globalModal.support_text"
                      ns="common"
                      components={{
                        a: (
                          <a
                            href={SUPPORT_URL}
                            className="text-white underline decoration-white/45 underline-offset-2"
                            onClick={(event) => event.stopPropagation()}
                          >
                            support
                          </a>
                        ),
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </motion.section>
        ) : null}
      </AnimatePresence>
    </div>
  );

  if (!portalRoot) return null;

  return createPortal(content, isDesktop ? portalRoot : document.body);
};

export default GlobalMessageModal;
